import React, { useState, useEffect, useRef, useCallback } from 'react'; 
import { toast } from 'react-toastify';
// import facilityService from '../services/facilityService'; // Not used
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { InformationCircleIcon, ArrowUturnLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import azureLogo from '../assets/model-logos/azure-openai-logo.svg';
import xAILogo from '../assets/model-logos/xai-logo.svg';

const getProviderLogo = (providerName) => {
  if (!providerName) return azureLogo; 
  const lowerCaseName = providerName.toLowerCase();
  
  if (lowerCaseName.includes('azure')) return azureLogo;
  if (lowerCaseName.includes('xai')) return xAILogo;
  // Add more mappings as needed
  // if (lowerCaseName.includes('openai')) return openaiLogo;
  // if (lowerCaseName.includes('meta')) return metaLogo;
  // if (lowerCaseName.includes('claude')) return claudeLogo;
  
  return azureLogo; 
};


const ToggleSwitch = ({ isActive, onToggle, disabled = false }) => (
  <div
    onClick={() => !disabled && onToggle()}
    className={`relative inline-flex items-center h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
      isActive ? 'bg-green-500' : 'bg-gray-300'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span
      aria-hidden="true"
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        isActive ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </div>
);

const InfoTooltip = ({ text }) => (
    <span className="relative group inline-flex items-center">
        <InformationCircleIcon
            className="h-4 w-4 text-gray-400 hover:text-gray-500 focus:text-gray-500 cursor-help"
            tabIndex={0}
        />
        <span
            role="tooltip"
            className="pointer-events-none absolute left-1/2 top-[125%] z-10 hidden w-max max-w-xs -translate-x-1/2 rounded-md bg-gray-800 px-2 py-1 text-xs text-white shadow-lg group-hover:block group-focus-within:block"
        >
            {text}
            <span className="absolute -top-1 left-1/2 -translate-x-1/2 h-2 w-2 rotate-45 bg-gray-800"></span>
        </span>
    </span>
);


export const ModelCard = ({ model, onUpdateSettings }) => {
  // Destructure all fields from the model prop
  const {
    id: facilityConfigId,
    display_name,
    display_description,
    provider_name,
    provider_logo_url,
    global_max_tokens,
    global_temperature,
    rate_limit, 
    is_assigned_to_facility,
    is_enabled_for_users, 
    is_default_for_facility,
    facility_temperature_override,
    facility_max_tokens_override,
  } = model;

  const isDisabled = !is_assigned_to_facility; // This is always false, but good to keep

  // --- LOCAL STATE (ONLY for text inputs) ---
  const [localTokens, setLocalTokens] = useState(facility_max_tokens_override || '');
  const [localTemp, setLocalTemp] = useState(facility_temperature_override || '');
  
  const [isLoading, setIsLoading] = useState(false); // For save button animation

  // Reset local state if the model prop changes
  useEffect(() => {
    setLocalTokens(model.facility_max_tokens_override || '');
    setLocalTemp(model.facility_temperature_override || '');
  }, [model]); // This hook is key!

  // "Dirty" Check (ONLY for text inputs) 
  const isDirty = 
    (localTokens || null) !== (facility_max_tokens_override || null) || 
    (localTemp || null) !== (facility_temperature_override || null);

  
  // IMMEDIATE ACTION Handlers
  
  const handleToggleStatus = () => {
    if (is_default_for_facility) return; // Use prop for check
    // This is an immediate action
    onUpdateSettings(facilityConfigId, { ...model, is_enabled_for_users: !is_enabled_for_users });
  };
  
  const handleSetDefault = () => {
    if (is_default_for_facility) return;
    // This is an immediate action
    // It will correctly send 'is_enabled_for_users: true' (or its current state)
    // because it's spreading the 'model' prop, not local state.
    onUpdateSettings(facilityConfigId, { ...model, is_default_for_facility: true });
  };

  // "DRAFT" ACTION Handlers

  const handleReset = () => {
    // Reset only the local text states
    setLocalTokens(facility_max_tokens_override || '');
    setLocalTemp(facility_temperature_override || '');
  };

  const handleSave = () => {
    //if (isDisabled) return;
    setIsLoading(true);

    // 1. Validate only local states
    const newTokens = localTokens === '' ? null : parseInt(localTokens, 10);
    const newTemp = localTemp === '' ? null : parseFloat(localTemp);

    if (newTokens !== null && (isNaN(newTokens) || newTokens < 0)) {
        toast.error("Invalid token value.");
        setIsLoading(false);
        return;
    }
    
    // Enforce global limit
    if (global_max_tokens && newTokens !== null && newTokens > global_max_tokens) {
        toast.error(`Tokens cannot exceed global limit of ${global_max_tokens}.`);
        setIsLoading(false);
        return;
    }
    
    if (newTemp !== null && (isNaN(newTemp) || newTemp < 0 || newTemp > 2)) {
        toast.error("Temperature must be between 0 and 2.");
        setIsLoading(false);
        return;
    }

    // 2. Build payload and call parent
    const payload = {
      ...model,
      facility_max_tokens_override: newTokens,
      facility_temperature_override: newTemp,
    };
    
    onUpdateSettings(facilityConfigId, payload)
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className={`relative p-5 bg-white border rounded-xl shadow-sm transition-all flex flex-col w-full hover:shadow-lg`}>
      
      {/* Header - Use model props directly */}
      <div className="grid grid-cols-[auto,1fr,auto,auto] items-center gap-3 w-full">
        <img 
            src={provider_logo_url || getProviderLogo(provider_name)} 
            alt={`${provider_name} logo`} 
            className="h-8 w-8 object-contain" 
        />
        <h3 className="min-w-0 truncate text-l font-extrabold text-gray-900" title={display_name}>
            {display_name}
        </h3>
        
        {is_default_for_facility ? (
          <span className="whitespace-nowrap inline-flex items-center gap-1 rounded-full border border-yellow-300 bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-800">
            <StarSolidIcon className="h-4 w-4 text-yellow-600" />
            Default
          </span>
        ) : <span className="hidden sm:block" />}
        
        <div className="justify-self-end">
          <ToggleSwitch 
            isActive={is_enabled_for_users} 
            onToggle={handleToggleStatus}
            disabled={is_default_for_facility} // Only disable if it's the default
          />
        </div>
      </div>
      
      <p className="mt-3 text-sm text-gray-600 flex-grow min-h-[40px]">
        {display_description || 'No description provided.'}
      </p>

      {/* "Cannot disable default" text */}
      {/* {is_default_for_facility && (
        <div 
          className="text-xs text-gray-500 text-right mt-1 -mb-3"
          title="You must set another model as default before you can disable this one."
        >
          Cannot disable default model
        </div>
      )} */}

      <hr className="my-4 border-t border-gray-200" />
      
      {/* Middle Section: Details - Inputs use local state */}
      <div className="flex-grow">
        <dl className="grid grid-cols-[140px,1fr] gap-y-4 text-sm">
          <dt className="font-semibold text-gray-900">Provider:</dt>
          <dd className="text-gray-700">{provider_name}</dd>

          <dt className="font-semibold text-gray-900">Rate Limit (Global):</dt>
          <dd className="flex items-center gap-2 text-gray-700">
            <span className="font-medium">{rate_limit ? rate_limit.toLocaleString() : 'Not Set'}</span>
            <span className="text-gray-700 flex-shrink-0">reqs/day</span>
            <InfoTooltip text="Set by Super Admin. Contact them to change." />
          </dd>

          {/* Max Tokens */}
          <dt className="font-semibold text-gray-900">Max Tokens:</dt>
          <dd className="flex items-center gap-2">
            <input
                type="text" 
                inputMode="decimal" 
                value={localTokens}
                placeholder={global_max_tokens ? global_max_tokens.toLocaleString() : 'Default'}
                onChange={(e) => setLocalTokens(e.target.value)}
                className="w-20 text-center border rounded px-2 py-0.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all border-gray-300"
            />
            <InfoTooltip text={`Global Limit: ${global_max_tokens ? global_max_tokens.toLocaleString() : 'N/A'}`} />
          </dd>
          
          {/* Temperature */}
          <dt className="font-semibold text-gray-900">Temperature:</dt>
          <dd className="flex items-center gap-2">
             <input
                type="text" 
                inputMode="decimal" 
                value={localTemp}
                placeholder={global_temperature !== null ? global_temperature : 'Default'}
                onChange={(e) => setLocalTemp(e.target.value)}
                className="w-20 text-center border rounded px-2 py-0.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all border-gray-300"
            />
            <InfoTooltip text={`Global Default: ${global_temperature !== null ? global_temperature : 'N/A'}`} />
          </dd>
        </dl>
      </div>

      {/* Button Section - Use model props */}
      <div className="mt-5 flex flex-col gap-2">
        <button
          onClick={handleSetDefault}
          disabled={is_default_for_facility}
          aria-disabled={is_default_for_facility}
          title={
            is_default_for_facility ? 'This model is already the default' 
            : 'Set this model as default'
          }
          className={`w-full py-2 text-sm font-semibold text-center rounded-md transition-colors
            ${is_default_for_facility
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
        >
          {is_default_for_facility ? 'Default Model' : 'Make Default'}
        </button>
      </div>
      
      {/* SAVE/CANCEL BUTTON GROUP - Only appears for text input changes */}
      {isDirty && (
        <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-center gap-3">
            <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-gray-700 rounded-md hover:bg-gray-100 disabled:opacity-50"
            >
                <ArrowUturnLeftIcon className="h-4 w-4" />
                Cancel
            </button>
            <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
            >
                {isLoading ? (
                    <svg className="animate-spin -ml-1 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                ) : (
                    <CheckCircleIcon className="-ml-1 h-5 w-5" />
                )}
                {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
        </div>
      )}
      {/* This button will be globalized soon */}
        {/* <button className="w-full py-2 text-sm font-semibold text-center text-green-700 bg-green-100 rounded-md hover:bg-green-200">
          Download Report
        </button> */}
    </div>
  );
};